import * as React from "react";

import { useCustomerCartId } from "../../../Common";

import { MyBagPresentationItem } from "../../model";

import { useDataMutationMyBagItemRemove_asController } from "../data-mutation--mybag-item-remove";

import { useMyBagOutOfStockPresentationModel } from "./my-bag-out-of-stock-presentation-model";

interface MyBagOutOfStockUserExperienceConsumerInterface {
  shouldIPresentAnOutOfStockDrawer: boolean;
  isLoadingRemoveAll_canOnlyRemoveOneItemForeachCall: boolean;
}
const defaultValue =
  {} as unknown as MyBagOutOfStockUserExperienceConsumerInterface;
const MyBagOutOfStockUserExperienceContext = React.createContext(defaultValue);

export interface MyBagOutOfStockUserExperience_ControllerInterface {
  handleClose: () => void;

  handleRemoveOutOfStockItem: (
    outOfStockItem: MyBagPresentationItem
  ) => Promise<void>;
  handleRemoveAllOutOfStockitems: () => Promise<void>;

  handleNeedsToShowOutOfStockDrawer: () => void;
}
const controllerDefaultValue =
  {} as unknown as MyBagOutOfStockUserExperience_ControllerInterface;
const MyBagOutOfStockUserExperience_ControllerContext = React.createContext(
  controllerDefaultValue
);

export const MyBagOutOfStockUserExperienceProvider: React.FunctionComponent = (
  props
) => {
  const { cartId } = useCustomerCartId();
  const { mutateAsync: removeAnItemFromMyBag } =
    useDataMutationMyBagItemRemove_asController();
  const { presentationOutOfStockItems } = useMyBagOutOfStockPresentationModel();

  const [shouldPresent, setShouldPresent] = React.useState(false);
  const [
    isLoadingRemoveAll_canOnlyRemoveOneItemForeachCall,
    setIsLoadingRemoveAll_canOnlyRemoveOneItemForeachCall,
  ] = React.useState(false);

  React.useEffect(() => {
    if (presentationOutOfStockItems.length < 1) {
      setShouldPresent(false);
      setIsLoadingRemoveAll_canOnlyRemoveOneItemForeachCall(false);
    }
  }, [presentationOutOfStockItems]);

  const handleNeedsToShowOutOfStockDrawer: MyBagOutOfStockUserExperience_ControllerInterface["handleNeedsToShowOutOfStockDrawer"] =
    React.useCallback(() => {
      setShouldPresent(true);
    }, [setShouldPresent]);

  const handleClose: MyBagOutOfStockUserExperience_ControllerInterface["handleClose"] =
    React.useCallback(() => {
      setShouldPresent(false);
    }, [setShouldPresent]);

  const handleRemoveOutOfStockItem: MyBagOutOfStockUserExperience_ControllerInterface["handleRemoveOutOfStockItem"] =
    React.useCallback(
      async (outOfStockItem) => {
        if (!cartId) {
          return;
        }

        try {
          await removeAnItemFromMyBag({
            cartId,
            cartItemToRemoveId: outOfStockItem.id,
          });
        } catch (error) {
          console.error(error);
        }
      },
      [cartId, removeAnItemFromMyBag]
    );

  const handleRemoveAllOutOfStockitems: MyBagOutOfStockUserExperience_ControllerInterface["handleRemoveAllOutOfStockitems"] =
    React.useCallback(async () => {
      if (!cartId) {
        return;
      }

      const promises = presentationOutOfStockItems.map((outOfStockItem) =>
        removeAnItemFromMyBag({ cartId, cartItemToRemoveId: outOfStockItem.id })
      );

      setIsLoadingRemoveAll_canOnlyRemoveOneItemForeachCall(true);
      await Promise.allSettled(promises);
      setIsLoadingRemoveAll_canOnlyRemoveOneItemForeachCall(false);
    }, [cartId, presentationOutOfStockItems, removeAnItemFromMyBag]);

  const value: MyBagOutOfStockUserExperienceConsumerInterface = React.useMemo(
    () => ({
      shouldIPresentAnOutOfStockDrawer: shouldPresent,
      isLoadingRemoveAll_canOnlyRemoveOneItemForeachCall,
    }),
    [shouldPresent, isLoadingRemoveAll_canOnlyRemoveOneItemForeachCall]
  );
  const controllerValue: MyBagOutOfStockUserExperience_ControllerInterface =
    React.useMemo(
      () => ({
        handleClose,

        handleRemoveOutOfStockItem,
        handleRemoveAllOutOfStockitems,

        handleNeedsToShowOutOfStockDrawer,
      }),
      [
        handleClose,
        handleRemoveOutOfStockItem,
        handleRemoveAllOutOfStockitems,
        handleNeedsToShowOutOfStockDrawer,
      ]
    );
  return (
    <MyBagOutOfStockUserExperience_ControllerContext.Provider
      value={controllerValue}
    >
      <MyBagOutOfStockUserExperienceContext.Provider value={value}>
        {props.children || []}
      </MyBagOutOfStockUserExperienceContext.Provider>
    </MyBagOutOfStockUserExperience_ControllerContext.Provider>
  );
};

export const useMyBagOutOfStockUserExperience = () => {
  const context = React.useContext(MyBagOutOfStockUserExperienceContext);
  if (context === defaultValue) {
    throw new Error(
      "useMyBagOutOfStockUserExperience must be used within a MyBagOutOfStockUserExperienceProvider"
    );
  }
  return context;
};

export const useMyBagOutOfStockUserExperience_asController = () => {
  const context = React.useContext(
    MyBagOutOfStockUserExperience_ControllerContext
  );
  if (context === controllerDefaultValue) {
    throw new Error(
      "useMyBagOutOfStockUserExperience_asController must be used within a MyBagOutOfStockUserExperienceProvider"
    );
  }
  return context;
};
